/**
 * nativeBridge
 * -----------------------------------------------------------------------------
 * Capa base de abstracción del bridge nativo (WebView Android/iOS).
 *
 * El wrapper nativo expone funciones globales `tiger*`. La forma de INVOCARLAS
 * difiere por plataforma:
 *   - Android: window.Jph.metodo(args)
 *   - iOS:     window.webkit.messageHandlers.JphIOS.postMessage({ action, params })
 *
 * Las RESPUESTAS del nativo no son retornos de la invocación: llegan como
 * funciones globales únicas en `window` (ej. window.tigerGetPicture(base64)),
 * de forma asíncrona y SIN estar atadas a quién las pidió.
 *
 * SCOPE DE ESTE MÓDULO:
 *   Solo cubre callbacks de tipo request -> response de un disparo (one-shot):
 *   se pide algo y el nativo responde UNA vez. Los callbacks de tipo stream
 *   (ej. geolocalización periódica que emite múltiples veces) NO entran acá;
 *   van a necesitar una API de suscripción aparte.
 *
 * JavaScript puro, sin dependencias externas. Guards defensivos para SSR.
 * -----------------------------------------------------------------------------
 */

/**
 * Pendientes one-shot por nombre de callback global.
 * @type {Map<string, { resolve: Function, reject: Function, timeoutId: any }>}
 */
const pendingCallbacks = new Map();

/**
 * Nombres de callback cuyo handler global ya fue instalado en window.
 * Garantiza idempotencia: el handler se instala una sola vez por nombre.
 * @type {Set<string>}
 */
const installedCallbacks = new Set();

/**
 * Guard SSR: indica si estamos en un entorno con `window` disponible.
 * @returns {boolean}
 */
function hasWindow() {
  return typeof window !== 'undefined';
}

/**
 * Detecta la plataforma de ejecución actual.
 *
 * - 'ios'     si el userAgent es iPhone/iPad Y existe window.webkit.messageHandlers.JphIOS
 * - 'android' si el userAgent es Android Y existe window.Jph
 * - 'web'     en cualquier otro caso (navegador móvil sin wrapper, desktop o SSR)
 *
 * @returns {'android' | 'ios' | 'web'}
 */
export function getPlatform() {
  // Sin window (SSR) no hay bridge nativo posible.
  if (!hasWindow()) {
    return 'web';
  }

  const userAgent = navigator.userAgent || '';

  // iOS: userAgent de iPhone/iPad + handler de mensajes JphIOS disponible.
  if (/iPhone|iPad/i.test(userAgent) && window.webkit?.messageHandlers?.JphIOS) {
    return 'ios';
  }

  // Android: userAgent de Android + objeto Jph inyectado por el wrapper.
  if (/Android/i.test(userAgent) && typeof window.Jph !== 'undefined') {
    return 'android';
  }

  return 'web';
}

/**
 * Indica si la app corre dentro de un wrapper nativo (Android o iOS).
 * @returns {boolean}
 */
export function isNativeApp() {
  const platform = getPlatform();
  return platform === 'android' || platform === 'ios';
}

/**
 * Envía un mensaje al bridge de iOS (JphIOS) de forma segura.
 *
 * No retorna la respuesta del nativo: la respuesta llega de forma asíncrona
 * por un callback global (ver awaitNativeCallback).
 *
 * @param {string} action          Nombre de la acción a ejecutar en el nativo.
 * @param {Object} [params={}]      Parámetros asociados a la acción.
 * @returns {void}
 */
export function iosPostMessage(action, params = {}) {
  const handler = hasWindow() ? window.webkit?.messageHandlers?.JphIOS : undefined;

  if (!handler) {
    console.warn(
      `[nativeBridge] iosPostMessage: JphIOS no disponible. Se ignora la acción "${action}".`
    );
    return;
  }

  handler.postMessage({ action, params });
}

/**
 * Invoca un método del bridge de Android (window.Jph) de forma segura.
 *
 * No retorna la respuesta del nativo: la respuesta llega de forma asíncrona
 * por un callback global (ver awaitNativeCallback).
 *
 * @param {string} methodName       Nombre del método expuesto en window.Jph.
 * @param {...any} args             Argumentos a pasar al método nativo.
 * @returns {void}
 */
export function androidCall(methodName, ...args) {
  const jph = hasWindow() ? window.Jph : undefined;

  if (typeof jph === 'undefined' || typeof jph[methodName] !== 'function') {
    console.warn(
      `[nativeBridge] androidCall: window.Jph.${methodName} no disponible. Se ignora la invocación.`
    );
    return;
  }

  jph[methodName](...args);
}

/**
 * Limpia y elimina el pendiente asociado a un nombre de callback.
 * No desinstala el handler global (se reutiliza en futuras solicitudes).
 *
 * @param {string} globalFnName
 * @returns {void}
 */
function clearPending(globalFnName) {
  const pending = pendingCallbacks.get(globalFnName);
  if (pending && pending.timeoutId) {
    clearTimeout(pending.timeoutId);
  }
  pendingCallbacks.delete(globalFnName);
}

/**
 * Instala (una sola vez por nombre) el handler global que el nativo invoca
 * como respuesta. El handler enruta los argumentos recibidos al pendiente
 * actual de ese nombre.
 *
 * @param {string} globalFnName
 * @returns {void}
 */
function installGlobalHandler(globalFnName) {
  if (installedCallbacks.has(globalFnName)) {
    return;
  }

  window[globalFnName] = function nativeBridgeCallback(...args) {
    const pending = pendingCallbacks.get(globalFnName);

    // Respuesta huérfana: llegó sin pendiente activo (ej. respuesta tardía
    // posterior a un timeout, o un disparo del nativo sin await previo).
    if (!pending) {
      console.warn(
        `[nativeBridge] Callback "${globalFnName}" recibido sin solicitud pendiente. Se ignora.`
      );
      return;
    }

    // Resolución segun cantidad de argumentos:
    //   0 args  -> undefined (ej. confirmaciones "ok/listo" sin payload)
    //   1 arg   -> ese valor
    //   >1 args -> array con todos los argumentos
    let value;
    if (args.length === 0) {
      value = undefined;
    } else if (args.length === 1) {
      value = args[0];
    } else {
      value = args;
    }

    clearPending(globalFnName);
    pending.resolve(value);
  };

  installedCallbacks.add(globalFnName);
}

/**
 * Espera la respuesta one-shot del nativo a través de una función global.
 *
 * IMPORTANTE — ORDEN DE USO OBLIGATORIO:
 *   El consumidor SIEMPRE debe registrar el await ANTES de disparar el método
 *   nativo. El handler global se instala de forma síncrona dentro de esta
 *   función, y el nativo puede responder muy rápido; si se dispara primero,
 *   la respuesta podría llegar antes de que exista el pendiente y perderse.
 *
 * @example
 *   const p = awaitNativeCallback('tigerGetPicture'); // 1º registra el await
 *   androidCall('tigerStartCamera');                  // 2º dispara el nativo
 *   const base64 = await p;                            // 3º espera la respuesta
 *
 * Política de pendiente único por nombre: si ya hay una promesa pendiente para
 * ese `globalFnName` y se pide otra, la anterior se rechaza con
 * Error('callback reemplazado por nueva solicitud') y la nueva queda pendiente.
 *
 * Resolución (segun argumentos recibidos en el callback):
 *   0 args  -> resolve(undefined)
 *   1 arg   -> resolve(args[0])
 *   >1 args -> resolve(args)  // array con todos los argumentos
 *
 * @param {string} globalFnName              Nombre de la función global que el
 *                                           nativo invoca como respuesta
 *                                           (ej. 'tigerGetPicture').
 * @param {Object} [options]
 * @param {number} [options.timeoutMs=60000] Milisegundos hasta rechazar por timeout.
 * @returns {Promise<any>} Resuelve con el/los argumento(s) del callback.
 */
export function awaitNativeCallback(globalFnName, { timeoutMs = 60000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!hasWindow()) {
      const err = new Error('nativeBridge no disponible (sin window)');
      err.code = 'NO_BRIDGE';
      reject(err);
      return;
    }

    // Pendiente único por nombre: si ya había uno, lo rechazamos para que la
    // nueva solicitud sea la unica vigente.
    const previous = pendingCallbacks.get(globalFnName);
    if (previous) {
      if (previous.timeoutId) {
        clearTimeout(previous.timeoutId);
      }
      const err = new Error('callback reemplazado por nueva solicitud');
      err.code = 'REPLACED';
      previous.reject(err);
      pendingCallbacks.delete(globalFnName);
    }

    // Aseguramos el handler global (idempotente) antes de registrar el pendiente.
    installGlobalHandler(globalFnName);

    // Timeout: si el nativo no responde a tiempo, limpiamos y rechazamos.
    const timeoutId = setTimeout(() => {
      clearPending(globalFnName);
      const err = new Error('timeout esperando ' + globalFnName);
      err.code = 'TIMEOUT';
      reject(err);
    }, timeoutMs);

    pendingCallbacks.set(globalFnName, { resolve, reject, timeoutId });
  });
}
